import CloseIcon from '@mui/icons-material/Close';
import Notifier from '@components/common/Notifier';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  JIRA_KEY_RE,
  PL_NUMBER_INVALID_MESSAGE,
  formatErrorMsg,
  getError,
} from '@account/utils/errors';
import {PL_NUMBER_VALIDATION_REGEX} from '@account/utils/validation';
import {renderQuota} from '@account/utils/quota';
import {useAdjustProductAccessUsage} from '@hooks/data/products/useAdjustProductAccessUsage';

// Same reason set used by the legacy Plan adjustment tool — these are the
// established usage-adjustment rationales.
const REASON_TYPES = [
  'Accidental Usage',
  'Converting Quota To Another Product',
  'E&R Complimentary Reset',
  'Pending Renewal',
  'Planeeter Usage',
  'Tasked Imagery Dispute Resolution',
  'Tasking With Manual Tesselation',
  'Technical Issue',
  'Other',
];

/**
 * Usage Reversal — apply a signed usage adjustment to the Usage API bucket
 * behind a workspace ProductAccess. "Reverse" posts a NEGATIVE amount that
 * decrements recorded usage (shows as a reversal on customer reporting); "Add"
 * posts a positive amount. The adjustment is bucket-level, so for pooled PU/MC
 * products the Usage API recomputes per-product attribution.
 */
const WorkspaceUsageReversalModal = ({open, onClose, productAccess}) => {
  const [direction, setDirection] = useState('reverse');
  const [amountRaw, setAmountRaw] = useState('');
  const [lineItemIdRaw, setLineItemIdRaw] = useState('');
  const [reason, setReason] = useState('');
  const [plNumber, setPlNumber] = useState('');
  const [supportTicketRef, setSupportTicketRef] = useState('');
  const [notes, setNotes] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);

  const units = productAccess.quota_units;
  const currentUsed =
    productAccess.quota_used === null || productAccess.quota_used === undefined
      ? null
      : Number(productAccess.quota_used);

  const magnitude = amountRaw === '' ? null : Number(amountRaw);
  const isInvalidNumber = magnitude !== null && isNaN(magnitude);
  const isFloat =
    magnitude !== null && !isNaN(magnitude) && !Number.isInteger(magnitude);
  const isNonPositive = magnitude !== null && magnitude <= 0;
  // Reverse reduces recorded usage → negative amount.
  const signedAmount =
    magnitude === null || isInvalidNumber
      ? null
      : direction === 'reverse'
        ? -Math.abs(magnitude)
        : Math.abs(magnitude);

  // pda-admin now requires the adjustment to name the line item it applies to
  // (QUID-60). IDs are positive integers; parse here so "12.5" or "abc" is
  // caught client-side instead of round-tripping to a 422.
  const lineItemId = lineItemIdRaw === '' ? null : Number(lineItemIdRaw);
  const lineItemIdValid =
    lineItemId !== null && Number.isInteger(lineItemId) && lineItemId > 0;

  const plValid = PL_NUMBER_VALIDATION_REGEX.test(plNumber.trim());
  const ticketValid = JIRA_KEY_RE.test(supportTicketRef.trim());
  // Mirror the backend Length(5, 500) so a too-short/too-long note is caught
  // client-side instead of round-tripping to a 422.
  const noteLen = notes.trim().length;
  const noteValid = noteLen >= 5 && noteLen <= 500;

  const canSubmit =
    signedAmount !== null &&
    !isFloat &&
    !isNonPositive &&
    lineItemIdValid &&
    !!reason &&
    plValid &&
    ticketValid &&
    noteValid;

  const {mutate, isLoading} = useAdjustProductAccessUsage(productAccess.id);

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleSubmit = () => {
    setErrorMsg(null);
    mutate(
      {
        amount: signedAmount,
        line_item_id: lineItemId,
        reason,
        note: notes.trim(),
        support_ticket_ref: supportTicketRef.trim(),
        pl_number: plNumber.trim(),
      },
      {
        onSuccess: () => onClose(),
        onError: err => setErrorMsg(formatErrorMsg(getError(err))),
      }
    );
  };

  const projectedUsed =
    currentUsed !== null && signedAmount !== null && !isNonPositive
      ? // Recorded usage can't go below zero; the projection is also approximate
        // for pooled buckets (the Usage API reattributes across sibling PAs).
        Math.max(0, currentUsed + signedAmount)
      : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, clickReason) => {
          if (clickReason === 'backdropClick') return;
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Usage Reversal
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={isLoading}
            sx={{position: 'absolute', right: 8, top: 8}}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{mt: 1}}>
            <Typography variant="body2" color="text.secondary">
              Adjusts recorded usage for{' '}
              <strong>{productAccess.title || productAccess.name}</strong> on
              the Usage API. Applies at the bucket level and shows as a usage
              adjustment on customer reporting.
            </Typography>

            <ToggleButtonGroup
              exclusive
              color="primary"
              value={direction}
              onChange={(_, val) => val && setDirection(val)}
              size="small"
              fullWidth
            >
              <ToggleButton value="reverse" data-qa-id="usage-reverse">
                Reverse (reduce usage)
              </ToggleButton>
              <ToggleButton value="add" data-qa-id="usage-add">
                Add usage
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              label="Line item ID"
              type="number"
              value={lineItemIdRaw}
              onChange={e => {
                setLineItemIdRaw(e.target.value);
                setErrorMsg(null);
              }}
              inputProps={{min: 1, step: 1}}
              error={lineItemIdRaw !== '' && !lineItemIdValid}
              helperText={
                lineItemIdRaw !== '' && !lineItemIdValid
                  ? 'Enter a positive whole number.'
                  : 'The line item this adjustment applies to.'
              }
              fullWidth
              required
              data-qa-id="usage-line-item-id"
            />

            <TextField
              label="Amount"
              type="number"
              value={amountRaw}
              onChange={e => {
                setAmountRaw(e.target.value);
                setErrorMsg(null);
              }}
              InputProps={{
                endAdornment: units ? (
                  <InputAdornment position="end">{units}</InputAdornment>
                ) : undefined,
              }}
              error={isInvalidNumber || isFloat || isNonPositive}
              helperText={
                isInvalidNumber
                  ? 'Enter a valid number.'
                  : isFloat
                    ? 'Enter a whole number.'
                    : isNonPositive
                      ? 'Enter a positive amount.'
                      : 'Amount to apply, as a positive number.'
              }
              fullWidth
              required
            />

            <Box
              sx={theme => ({
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                p: 2,
                bgcolor: 'grey.50',
              })}
            >
              <Typography variant="subtitle2" display="block" mb={1}>
                Usage preview
              </Typography>
              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Recorded usage (this product)
                  </Typography>
                  <Typography variant="body2">
                    {currentUsed !== null
                      ? renderQuota(currentUsed, units)
                      : 'Unavailable'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Change
                  </Typography>
                  <Typography
                    variant="body2"
                    color={
                      signedAmount === null || isNonPositive
                        ? 'text.primary'
                        : signedAmount < 0
                          ? 'success.main'
                          : 'warning.main'
                    }
                  >
                    {signedAmount === null || isNonPositive
                      ? '—'
                      : `${signedAmount > 0 ? '+' : ''}${signedAmount.toLocaleString()} ${units ?? ''}`}
                  </Typography>
                </Stack>
                <Divider sx={{my: 0.5}} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={600}>
                    Projected usage
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {projectedUsed !== null
                      ? renderQuota(projectedUsed, units)
                      : '—'}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <TextField
              select
              label="Reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              fullWidth
              required
              data-qa-id="usage-reason"
            >
              {REASON_TYPES.map(r => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Opportunity ID"
              value={plNumber}
              onChange={e => setPlNumber(e.target.value)}
              placeholder="PL-1234567"
              error={!!plNumber.trim() && !plValid}
              helperText={
                plNumber.trim() && !plValid
                  ? PL_NUMBER_INVALID_MESSAGE
                  : 'Format: PL-XXXXXXX, PL-OTHER or PL-OVERAGE'
              }
              fullWidth
              required
            />

            <TextField
              label="Support ticket ref"
              value={supportTicketRef}
              onChange={e => setSupportTicketRef(e.target.value)}
              placeholder="CGO-1234"
              error={!!supportTicketRef.trim() && !ticketValid}
              helperText={
                supportTicketRef.trim() && !ticketValid
                  ? 'Must match format PROJECT-1234 (e.g. CGO-1234)'
                  : 'Format: PROJECT-1234 (e.g. CGO-1234)'
              }
              fullWidth
              required
            />

            <TextField
              label="Notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              multiline
              minRows={3}
              error={noteLen > 0 && !noteValid}
              helperText={
                noteLen > 500
                  ? 'Notes must be 500 characters or fewer.'
                  : noteLen > 0 && noteLen < 5
                    ? 'Notes must be at least 5 characters.'
                    : ' '
              }
              inputProps={{maxLength: 500}}
              fullWidth
              required
            />

            {errorMsg && (
              <Typography color="error" variant="body2">
                {errorMsg}
              </Typography>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            variant="contained"
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? 'Saving…' : 'Apply adjustment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Notifier
        open={isLoading}
        message="Applying usage adjustment…"
        progress
      />
    </>
  );
};

WorkspaceUsageReversalModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  productAccess: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string,
    name: PropTypes.string,
    quota_units: PropTypes.string,
    quota_used: PropTypes.number,
  }).isRequired,
};

export default WorkspaceUsageReversalModal;
